import { apiSlice } from "@/shared/api/api-slice"
import type {
  ButtonEmojiCaptureFeature,
  ButtonEmojiCaptureResult,
  MessageCaptureIntent
} from "@/shared/types/api/moderation.types"

import {
  AutoMessageResponse as GetAutoMessageResponse,
  AutoMessageCaptureIntent,
  AutoMessageCaptureIntentResponse,
  AutoMessageSchema,
  AutoMessagesListResponse as GetChatAutoMessagesResponse,
  CreateAutoMessageCaptureIntentRequest,
  FarewellResponse,
  FarewellCaptureIntent,
  FarewellCaptureIntentResponse,
  FarewellSchema,
  GreetingResponse,
  GreetingCaptureIntent,
  GreetingCaptureIntentResponse,
  GreetingSchema,
  LeavePunishmentResponse,
  LeavePunishmentSettings,
  MessageCaptureActivation,
  MessageCaptureActivationResponse,
  MessageCaptureFeature,
  PatchAutoMessageRequest,
  PatchFarewellMessageRequest,
  PatchGreetingMessageRequest,
  PatchRulesMessageRequest,
  ReportsResponse as GetReportsResponse,
  ReportsSchema,
  RulesMessageResponse as GetRulesMessageResponse,
  QuietModeCaptureIntent,
  QuietModeCaptureIntentResponse,
  RulesCaptureIntent,
  RulesCaptureIntentResponse,
  RulesMessageSchema,
  GetQuietModeResponse,
  UpdateQuietModeMessageRequest,
  UpdateQuietModeMessageResponse,
  UpdateQuietModeRequest,
  QuietModeBoundary,
  QuietModeSchema,
  UpdateQuietModeResponse,
  UpsertAutoMessageRequest,
  UpsertAutoMessageResponse,
  UpsertFarewellMessageRequest,
  UpsertGreetingMessageRequest,
  UpsertLeavePunishmentRequest,
  UpsertReportsRequest,
  UpsertReportsResponse,
  UpsertRulesMessageRequest,
  UpsertRulesResponse as UpsertRulesMessageResponse
} from "@/shared/types"

type HTMLValidationError = {
  message: string
  position: number | null
}

type ValidateMarkupRequest = {
  text: string
}

type ValidateMarkupSuccessResponse = {
  ok: true
  result: { valid: true }
}

type ValidateMarkupErrorResponse = {
  ok: false
  error: HTMLValidationError[]
}

export type ValidateMarkupResponse = ValidateMarkupSuccessResponse | ValidateMarkupErrorResponse

/**
 * Moderation API endpoints with proper cache invalidation tags
 */
export const moderationApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getGreeting: builder.query<GreetingSchema | null, number>({
      query: chatId => `/api/v1/moderation/greeting/${chatId}`,
      transformResponse: (response: GreetingResponse) => {
        return response.result.greeting
      },
      providesTags: (_result, _error, chatId) => [{ type: "Greeting", id: chatId }]
    }),

    getQuietMode: builder.query<QuietModeSchema, number>({
      query: chatId => `/api/v1/moderation/quiet-mode/${chatId}`,
      transformResponse: (response: GetQuietModeResponse) => response.result,
      providesTags: (_result, _error, chatId) => [{ type: "QuietMode", id: chatId }]
    }),

    updateQuietMode: builder.mutation<UpdateQuietModeResponse, { chatId: number; request: UpdateQuietModeRequest }>({
      query: ({ chatId, request }) => ({
        url: `/api/v1/moderation/quiet-mode/${chatId}`,
        method: "PATCH",
        body: request
      }),
      invalidatesTags: (_, error, { chatId }) =>
        error
          ? []
          : [
              { type: "QuietMode", id: chatId },
              { type: "FeaturesStatus", id: chatId }
            ]
    }),

    updateQuietModeMessage: builder.mutation<
      UpdateQuietModeMessageResponse,
      {
        chatId: number
        boundary: QuietModeBoundary
        request: UpdateQuietModeMessageRequest
        file?: File | null
      }
    >({
      query: ({ chatId, boundary, request, file }) => {
        const formData = new FormData()
        if (file) {
          formData.append("file", file)
        }
        formData.append("data", JSON.stringify(request))
        return {
          url: `/api/v1/moderation/quiet-mode/${chatId}/message/${boundary}`,
          method: "PATCH",
          body: formData
        }
      },
      invalidatesTags: (_, error, { chatId }) => (error ? [] : [{ type: "QuietMode", id: chatId }])
    }),

    createRulesCaptureIntent: builder.mutation<RulesCaptureIntent, { chatId: number; locale: string }>({
      query: ({ chatId, locale }) => ({
        url: `/api/v1/moderation/rules/${chatId}/configure-in-telegram`,
        method: "POST",
        body: { locale }
      }),
      transformResponse: (response: RulesCaptureIntentResponse) => response.result
    }),

    createButtonEmojiCaptureIntent: builder.mutation<
      MessageCaptureIntent,
      { chatId: number; feature: ButtonEmojiCaptureFeature; locale: string }
    >({
      query: ({ chatId, feature, locale }) => ({
        url: `/api/v1/moderation/button-emoji/${chatId}/configure-in-telegram`,
        method: "POST",
        body: { feature, locale }
      }),
      transformResponse: (response: { result: MessageCaptureIntent }) => response.result
    }),
    getButtonEmojiCapture: builder.query<
      ButtonEmojiCaptureResult,
      { chatId: number; feature: ButtonEmojiCaptureFeature; token: string }
    >({
      query: ({ chatId, feature, token }) => ({
        url: `/api/v1/moderation/button-emoji/${chatId}/configure-in-telegram/${token}`,
        params: { feature }
      }),
      transformResponse: (response: { result: ButtonEmojiCaptureResult }) => response.result,
      keepUnusedDataFor: 0
    }),
    cancelButtonEmojiCaptureIntent: builder.mutation<
      undefined,
      { chatId: number; feature: ButtonEmojiCaptureFeature; token: string }
    >({
      query: ({ chatId, feature, token }) => ({
        url: `/api/v1/moderation/button-emoji/${chatId}/configure-in-telegram/${token}`,
        params: { feature },
        method: "DELETE"
      })
    }),
    activateMessageCaptureIntent: builder.mutation<
      MessageCaptureActivation,
      { feature: MessageCaptureFeature; chatId: number; token: string; params?: { feature: ButtonEmojiCaptureFeature } }
    >({
      query: ({ feature, chatId, token, params }) => ({
        url: `/api/v1/moderation/${feature}/${chatId}/configure-in-telegram/${token}/activate`,
        params,
        method: "POST"
      }),
      transformResponse: (response: MessageCaptureActivationResponse) => response.result
    }),

    createQuietModeCaptureIntent: builder.mutation<
      QuietModeCaptureIntent,
      { chatId: number; boundary: QuietModeBoundary; locale: string }
    >({
      query: ({ chatId, boundary, locale }) => ({
        url: `/api/v1/moderation/quiet-mode/${chatId}/configure-in-telegram`,
        method: "POST",
        body: { boundary, locale }
      }),
      transformResponse: (response: QuietModeCaptureIntentResponse) => response.result
    }),

    createGreetingCaptureIntent: builder.mutation<GreetingCaptureIntent, { chatId: number; locale: string }>({
      query: ({ chatId, locale }) => ({
        url: `/api/v1/moderation/greeting/${chatId}/configure-in-telegram`,
        method: "POST",
        body: { locale }
      }),
      transformResponse: (response: GreetingCaptureIntentResponse) => response.result
    }),

    configureGreetingWithStatus: builder.mutation<
      GreetingResponse,
      {
        chatId: number
        request: UpsertGreetingMessageRequest
        file?: File
        enabledState?: boolean
      }
    >({
      queryFn: async ({ chatId, request, file, enabledState }, _api, _extraOptions, baseQuery) => {
        const formData = new FormData()
        if (file) {
          formData.append("file", file)
        }
        formData.append("data", JSON.stringify(request))

        const configResult = await baseQuery({
          url: `/api/v1/moderation/greeting/${chatId}`,
          method: "PUT",
          body: formData
        })

        if (configResult.error) {
          return { error: configResult.error }
        }

        if (enabledState !== undefined) {
          const toggleUrl = enabledState
            ? `/api/v1/moderation/greeting/${chatId}/enable`
            : `/api/v1/moderation/greeting/${chatId}/disable`

          const toggleResult = await baseQuery({
            url: toggleUrl,
            method: "PATCH"
          })

          if (toggleResult.error) {
            return { error: toggleResult.error }
          }
        }

        return configResult as { data: GreetingResponse }
      },
      invalidatesTags: (_, error, { chatId }) =>
        error
          ? []
          : [
              { type: "Greeting", id: chatId },
              { type: "FeaturesStatus", id: chatId }
            ]
    }),

    configureGreeting: builder.mutation<
      GreetingResponse,
      { chatId: number; request: UpsertGreetingMessageRequest; file?: File }
    >({
      query: ({ chatId, request, file }) => {
        const formData = new FormData()
        if (file) {
          formData.append("file", file)
        }
        formData.append("data", JSON.stringify(request))

        return {
          url: `/api/v1/moderation/greeting/${chatId}`,
          method: "PUT",
          body: formData
        }
      },
      invalidatesTags: (_, error, { chatId }) => (error ? [] : [{ type: "Greeting", id: chatId }])
    }),

    uploadGreetingMedia: builder.mutation<GreetingResponse, { chatId: number; file: File | null }>({
      query: ({ chatId, file }) => {
        const formData = new FormData()
        if (file) {
          formData.append("file", file)
        }
        const request: PatchGreetingMessageRequest = file === null ? { clear_media: true } : {}
        formData.append("data", JSON.stringify(request))

        return {
          url: `/api/v1/moderation/greeting/${chatId}`,
          method: "PATCH",
          body: formData
        }
      },
      invalidatesTags: (_, error, { chatId }) => (error ? [] : [{ type: "Greeting", id: chatId }])
    }),

    updateGreetingSettings: builder.mutation<
      GreetingResponse,
      { chatId: number; request: PatchGreetingMessageRequest }
    >({
      query: ({ chatId, request }) => {
        const formData = new FormData()
        formData.append("data", JSON.stringify(request))

        return {
          url: `/api/v1/moderation/greeting/${chatId}`,
          method: "PATCH",
          body: formData
        }
      },
      invalidatesTags: (_, error, { chatId }) => (error ? [] : [{ type: "Greeting", id: chatId }])
    }),

    deleteGreeting: builder.mutation<undefined, number>({
      query: chatId => ({
        url: `/api/v1/moderation/greeting/${chatId}`,
        method: "DELETE"
      }),
      invalidatesTags: (_, error, chatId) =>
        error
          ? []
          : [
              { type: "Greeting", id: chatId },
              { type: "GreetingUpdate", id: chatId },
              { type: "Greeting", id: "LIST" },
              { type: "GreetingUpdate", id: "LIST" },
              { type: "Greeting", id: `${chatId}` },
              { type: "Greeting", id: `LIST-${chatId}` }
            ]
    }),

    enableGreeting: builder.mutation<undefined, number>({
      query: chatId => ({
        url: `/api/v1/moderation/greeting/${chatId}/enable`,
        method: "PATCH"
      }),
      invalidatesTags: (_, error, chatId) =>
        error
          ? []
          : [
              { type: "Greeting", id: chatId },
              { type: "FeaturesStatus", id: chatId }
            ]
    }),

    disableGreeting: builder.mutation<undefined, number>({
      query: chatId => ({
        url: `/api/v1/moderation/greeting/${chatId}/disable`,
        method: "PATCH"
      }),
      invalidatesTags: (_, error, chatId) =>
        error
          ? []
          : [
              { type: "Greeting", id: chatId },
              { type: "FeaturesStatus", id: chatId }
            ]
    }),

    getFarewell: builder.query<FarewellSchema | null, number>({
      query: chatId => `/api/v1/moderation/farewell/${chatId}`,
      transformResponse: (response: FarewellResponse) => {
        return response.result.farewell
      },
      providesTags: (_result, _error, chatId) => [{ type: "Farewell", id: chatId }]
    }),

    createFarewellCaptureIntent: builder.mutation<FarewellCaptureIntent, { chatId: number; locale: string }>({
      query: ({ chatId, locale }) => ({
        url: `/api/v1/moderation/farewell/${chatId}/configure-in-telegram`,
        method: "POST",
        body: { locale }
      }),
      transformResponse: (response: FarewellCaptureIntentResponse) => response.result
    }),

    configureFarewellWithStatus: builder.mutation<
      FarewellResponse,
      {
        chatId: number
        request: UpsertFarewellMessageRequest
        file?: File
        enabledState?: boolean
      }
    >({
      queryFn: async ({ chatId, request, file, enabledState }, _api, _extraOptions, baseQuery) => {
        const formData = new FormData()
        if (file) {
          formData.append("file", file)
        }
        formData.append("data", JSON.stringify(request))

        const configResult = await baseQuery({
          url: `/api/v1/moderation/farewell/${chatId}`,
          method: "PUT",
          body: formData
        })

        if (configResult.error) {
          return { error: configResult.error }
        }

        if (enabledState !== undefined) {
          const toggleUrl = enabledState
            ? `/api/v1/moderation/farewell/${chatId}/enable`
            : `/api/v1/moderation/farewell/${chatId}/disable`

          const toggleResult = await baseQuery({
            url: toggleUrl,
            method: "PATCH"
          })

          if (toggleResult.error) {
            return { error: toggleResult.error }
          }
        }

        return configResult as { data: FarewellResponse }
      },
      invalidatesTags: (_, error, { chatId }) =>
        error
          ? []
          : [
              { type: "Farewell", id: chatId },
              { type: "FeaturesStatus", id: chatId }
            ]
    }),

    configureFarewell: builder.mutation<
      FarewellResponse,
      { chatId: number; request: UpsertFarewellMessageRequest; file?: File }
    >({
      query: ({ chatId, request, file }) => {
        const formData = new FormData()
        if (file) {
          formData.append("file", file)
        }
        formData.append("data", JSON.stringify(request))

        return {
          url: `/api/v1/moderation/farewell/${chatId}`,
          method: "PUT",
          body: formData
        }
      },
      invalidatesTags: (_, error, { chatId }) => (error ? [] : [{ type: "Farewell", id: chatId }])
    }),

    updateFarewellSettings: builder.mutation<
      FarewellResponse,
      { chatId: number; request: PatchFarewellMessageRequest }
    >({
      query: ({ chatId, request }) => {
        const formData = new FormData()
        formData.append("data", JSON.stringify(request))

        return {
          url: `/api/v1/moderation/farewell/${chatId}`,
          method: "PATCH",
          body: formData
        }
      },
      invalidatesTags: (_, error, { chatId }) => (error ? [] : [{ type: "Farewell", id: chatId }])
    }),

    uploadFarewellMedia: builder.mutation<FarewellResponse, { chatId: number; file: File | null }>({
      query: ({ chatId, file }) => {
        const formData = new FormData()
        if (file) {
          formData.append("file", file)
        }
        const request: PatchFarewellMessageRequest = file === null ? { clear_media: true } : {}
        formData.append("data", JSON.stringify(request))

        return {
          url: `/api/v1/moderation/farewell/${chatId}`,
          method: "PATCH",
          body: formData
        }
      },
      invalidatesTags: (_, error, { chatId }) => (error ? [] : [{ type: "Farewell", id: chatId }])
    }),

    deleteFarewell: builder.mutation<undefined, number>({
      query: chatId => ({
        url: `/api/v1/moderation/farewell/${chatId}`,
        method: "DELETE"
      }),
      invalidatesTags: (_, error, chatId) =>
        error
          ? []
          : [
              { type: "Farewell", id: chatId },
              { type: "FarewellUpdate", id: chatId },
              { type: "Farewell", id: "LIST" },
              { type: "FarewellUpdate", id: "LIST" },
              { type: "Farewell", id: `${chatId}` },
              { type: "Farewell", id: `LIST-${chatId}` }
            ]
    }),

    enableFarewell: builder.mutation<undefined, number>({
      query: chatId => ({
        url: `/api/v1/moderation/farewell/${chatId}/enable`,
        method: "PATCH"
      }),
      invalidatesTags: (_, error, chatId) =>
        error
          ? []
          : [
              { type: "Farewell", id: chatId },
              { type: "FeaturesStatus", id: chatId }
            ]
    }),

    disableFarewell: builder.mutation<undefined, number>({
      query: chatId => ({
        url: `/api/v1/moderation/farewell/${chatId}/disable`,
        method: "PATCH"
      }),
      invalidatesTags: (_, error, chatId) =>
        error
          ? []
          : [
              { type: "Farewell", id: chatId },
              { type: "FeaturesStatus", id: chatId }
            ]
    }),

    getLeavePunishment: builder.query<LeavePunishmentSettings, number>({
      query: chatId => `/api/v1/moderation/leave-punishment/${chatId}`,
      transformResponse: (response: LeavePunishmentResponse) => response.result,
      providesTags: (_result, _error, chatId) => [{ type: "LeavePunishment", id: chatId }]
    }),

    updateLeavePunishment: builder.mutation<
      LeavePunishmentResponse,
      { chatId: number; request: UpsertLeavePunishmentRequest }
    >({
      query: ({ chatId, request }) => ({
        url: `/api/v1/moderation/leave-punishment/${chatId}`,
        method: "PUT",
        body: request
      }),
      invalidatesTags: (_result, error, { chatId }) => (error ? [] : [{ type: "LeavePunishment", id: chatId }])
    }),

    getRules: builder.query<RulesMessageSchema | null, number>({
      query: chatId => `/api/v1/moderation/rules/${chatId}`,
      transformResponse: (response: GetRulesMessageResponse) => {
        return response.result.rules
      },
      providesTags: (_result, _error, chatId) => [{ type: "Rules", id: chatId }]
    }),

    configureRulesWithStatus: builder.mutation<
      UpsertRulesMessageResponse,
      {
        chatId: number
        request: UpsertRulesMessageRequest
        file?: File
        enabledState?: boolean
      }
    >({
      queryFn: async ({ chatId, request, file, enabledState }, _api, _extraOptions, baseQuery) => {
        const formData = new FormData()
        if (file) {
          formData.append("file", file)
        }
        formData.append("data", JSON.stringify(request))

        const configResult = await baseQuery({
          url: `/api/v1/moderation/rules/${chatId}`,
          method: "PUT",
          body: formData
        })

        if (configResult.error) {
          return { error: configResult.error }
        }

        if (enabledState !== undefined) {
          const toggleUrl = enabledState
            ? `/api/v1/moderation/rules/${chatId}/enable`
            : `/api/v1/moderation/rules/${chatId}/disable`

          const toggleResult = await baseQuery({
            url: toggleUrl,
            method: "PATCH"
          })

          if (toggleResult.error) {
            return { error: toggleResult.error }
          }
        }

        return configResult as { data: UpsertRulesMessageResponse }
      },
      invalidatesTags: (_, error, { chatId }) =>
        error
          ? []
          : [
              { type: "Rules", id: chatId },
              { type: "FeaturesStatus", id: chatId }
            ]
    }),

    configureRules: builder.mutation<
      UpsertRulesMessageResponse,
      { chatId: number; request: UpsertRulesMessageRequest; file?: File }
    >({
      query: ({ chatId, request, file }) => {
        const formData = new FormData()
        if (file) {
          formData.append("file", file)
        }
        formData.append("data", JSON.stringify(request))

        return {
          url: `/api/v1/moderation/rules/${chatId}`,
          method: "PUT",
          body: formData
        }
      },
      invalidatesTags: (_, error, { chatId }) => (error ? [] : [{ type: "Rules", id: chatId }])
    }),

    updateRulesSettings: builder.mutation<
      UpsertRulesMessageResponse,
      { chatId: number; request: PatchRulesMessageRequest }
    >({
      query: ({ chatId, request }) => {
        const formData = new FormData()
        formData.append("data", JSON.stringify(request))

        return {
          url: `/api/v1/moderation/rules/${chatId}`,
          method: "PATCH",
          body: formData
        }
      },
      invalidatesTags: (_, error, { chatId }) => (error ? [] : [{ type: "Rules", id: chatId }])
    }),

    uploadRulesMedia: builder.mutation<UpsertRulesMessageResponse, { chatId: number; file: File | null }>({
      query: ({ chatId, file }) => {
        const formData = new FormData()
        if (file) {
          formData.append("file", file)
        }
        const request: PatchRulesMessageRequest = file === null ? { clear_media: true } : {}
        formData.append("data", JSON.stringify(request))

        return {
          url: `/api/v1/moderation/rules/${chatId}`,
          method: "PATCH",
          body: formData
        }
      },
      invalidatesTags: (_, error, { chatId }) => (error ? [] : [{ type: "Rules", id: chatId }])
    }),

    deleteRules: builder.mutation<undefined, number>({
      query: chatId => ({
        url: `/api/v1/moderation/rules/${chatId}`,
        method: "DELETE"
      }),
      invalidatesTags: (_, error, chatId) =>
        error
          ? []
          : [
              { type: "Rules", id: chatId },
              { type: "Rules", id: "LIST" }
            ]
    }),

    enableRules: builder.mutation<undefined, number>({
      query: chatId => ({
        url: `/api/v1/moderation/rules/${chatId}/enable`,
        method: "PATCH"
      }),
      invalidatesTags: (_, error, chatId) =>
        error
          ? []
          : [
              { type: "Rules", id: chatId },
              { type: "FeaturesStatus", id: chatId }
            ]
    }),

    disableRules: builder.mutation<undefined, number>({
      query: chatId => ({
        url: `/api/v1/moderation/rules/${chatId}/disable`,
        method: "PATCH"
      }),
      invalidatesTags: (_, error, chatId) =>
        error
          ? []
          : [
              { type: "Rules", id: chatId },
              { type: "FeaturesStatus", id: chatId }
            ]
    }),

    getAutoMessage: builder.query<GetAutoMessageResponse, { id: number; chatId: number }>({
      query: ({ id, chatId }) => `/api/v1/moderation/auto_message/chat/${chatId}/${id}`,
      providesTags: (_result, _error, { id }) => [{ type: "AutoMessage", id }]
    }),

    getChatAutoMessages: builder.query<AutoMessageSchema[], number>({
      query: chatId => `/api/v1/moderation/auto_message/chat/${chatId}`,
      transformResponse: (response: GetChatAutoMessagesResponse) => {
        return response.result.auto_messages
      },
      providesTags: (_result, _error, chatId) => [
        { type: "AutoMessage", id: `list-${chatId}` },
        { type: "AutoMessageUpdate", id: chatId },
        "AutoMessage"
      ]
    }),

    createAutoMessageCaptureIntent: builder.mutation<
      AutoMessageCaptureIntent,
      { chatId: number; request: CreateAutoMessageCaptureIntentRequest }
    >({
      query: ({ chatId, request }) => ({
        url: `/api/v1/moderation/auto_message/${chatId}/configure-in-telegram`,
        method: "POST",
        body: request
      }),
      transformResponse: (response: AutoMessageCaptureIntentResponse) => response.result
    }),

    configureAutoMessage: builder.mutation<
      UpsertAutoMessageResponse,
      { request: UpsertAutoMessageRequest; file?: File; files?: File[] }
    >({
      query: ({ request, file, files }) => {
        const formData = new FormData()
        // Support multiple files
        if (files && files.length > 0) {
          files.forEach(f => {
            formData.append("files", f)
          })
        } else if (file) {
          formData.append("files", file)
        }
        formData.append("data", JSON.stringify(request))

        return {
          url: `/api/v1/moderation/auto_message`,
          method: "PUT",
          body: formData
        }
      },
      invalidatesTags: (_, error, { request }) =>
        error
          ? []
          : [
              { type: "AutoMessage", id: request.id ?? "new" },
              { type: "AutoMessage", id: `list-${request.chat_id}` },
              { type: "AutoMessageUpdate", id: request.chat_id },
              { type: "FeaturesStatus", id: request.chat_id },
              "AutoMessage"
            ]
    }),

    deleteAutoMessage: builder.mutation<undefined, { id: number; chatId: number }>({
      query: ({ id, chatId }) => ({
        url: `/api/v1/moderation/auto_message/chat/${chatId}/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: async ({ id, chatId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          moderationApi.util.updateQueryData("getChatAutoMessages", chatId, draft => {
            const index = draft.findIndex(msg => msg.id === id)
            if (index !== -1) {
              draft.splice(index, 1)
            }
          })
        )

        try {
          await queryFulfilled
        } catch (error) {
          patchResult.undo()
          console.error("Failed to delete auto message:", { id, chatId, error })
        }
      },
      invalidatesTags: (_, error, { id, chatId }) =>
        error
          ? []
          : [
              { type: "AutoMessage", id },
              { type: "AutoMessage", id: `list-${chatId}` },
              { type: "AutoMessageUpdate", id: chatId },
              { type: "FeaturesStatus", id: chatId },
              "AutoMessage"
            ]
    }),

    pauseAutoMessage: builder.mutation<undefined, { id: number; chatId: number }>({
      query: ({ id, chatId }) => ({
        url: `/api/v1/moderation/auto_message/chat/${chatId}/${id}/pause`,
        method: "PATCH"
      }),
      invalidatesTags: (_, error, { id, chatId }) =>
        error
          ? []
          : [
              { type: "AutoMessage", id },
              { type: "AutoMessage", id: `list-${chatId}` },
              { type: "AutoMessageUpdate", id: chatId },
              { type: "FeaturesStatus", id: chatId },
              "AutoMessage"
            ]
    }),

    continueAutoMessage: builder.mutation<undefined, { id: number; chatId: number }>({
      query: ({ id, chatId }) => ({
        url: `/api/v1/moderation/auto_message/chat/${chatId}/${id}/continue`,
        method: "PATCH"
      }),
      invalidatesTags: (_, error, { id, chatId }) =>
        error
          ? []
          : [
              { type: "AutoMessage", id },
              { type: "AutoMessage", id: `list-${chatId}` },
              { type: "AutoMessageUpdate", id: chatId },
              { type: "FeaturesStatus", id: chatId },
              "AutoMessage"
            ]
    }),

    previewAutoMessageSchedule: builder.query<
      { next_scheduled_at: string | null },
      { chatId: number; messageId: number; request: Omit<PatchAutoMessageRequest, "id" | "chat_id"> }
    >({
      query: ({ chatId, messageId, request }) => ({
        url: `/api/v1/moderation/auto_message/chat/${chatId}/${messageId}/schedule-preview`,
        method: "POST",
        body: { ...request, id: messageId, chat_id: chatId } satisfies PatchAutoMessageRequest
      }),
      transformResponse: (response: { result: { next_scheduled_at: string | null } }) => response.result,
      providesTags: (_result, _error, { messageId }) => [{ type: "AutoMessage", id: messageId }]
    }),

    updateAutoMessageSettings: builder.mutation<
      UpsertAutoMessageResponse,
      { chatId: number; messageId: number; request: Omit<PatchAutoMessageRequest, "id" | "chat_id"> }
    >({
      query: ({ chatId, messageId, request }) => {
        const formData = new FormData()
        formData.append(
          "data",
          JSON.stringify({
            id: messageId,
            chat_id: chatId,
            ...request
          } satisfies PatchAutoMessageRequest)
        )

        return {
          url: `/api/v1/moderation/auto_message`,
          method: "PATCH",
          body: formData
        }
      },
      invalidatesTags: (_result, _error, { chatId, messageId }) => [
        { type: "AutoMessage", id: messageId },
        { type: "AutoMessage", id: `list-${chatId}` },
        { type: "AutoMessageUpdate", id: chatId },
        "AutoMessage"
      ]
    }),

    uploadAutoMessageMedia: builder.mutation<
      UpsertAutoMessageResponse,
      {
        chatId: number
        messageId: number
        file?: File | null
        files?: File[]
        clearMedia?: boolean
        retainedMediaUrls?: string[]
        mediaManifest?: string[]
      }
    >({
      query: ({ chatId, messageId, file, files, clearMedia, retainedMediaUrls, mediaManifest }) => {
        const formData = new FormData()
        for (const upload of files ?? (file ? [file] : [])) {
          formData.append("files", upload)
        }
        formData.append(
          "data",
          JSON.stringify({
            id: messageId,
            chat_id: chatId,
            ...(clearMedia || file === null ? { clear_media: true } : {}),
            ...(retainedMediaUrls ? { retained_media_urls: retainedMediaUrls } : {}),
            ...(mediaManifest ? { media_manifest: mediaManifest } : {})
          } satisfies PatchAutoMessageRequest)
        )

        return {
          url: `/api/v1/moderation/auto_message`,
          method: "PATCH",
          body: formData
        }
      },
      invalidatesTags: (_result, _error, { chatId, messageId }) => [
        { type: "AutoMessage", id: messageId },
        { type: "AutoMessage", id: `list-${chatId}` },
        { type: "AutoMessageUpdate", id: chatId },
        "AutoMessage"
      ]
    }),

    getReports: builder.query<ReportsSchema, number>({
      query: chatId => `/api/v1/moderation/reports/${chatId}`,
      transformResponse: (response: GetReportsResponse) => response.result.settings,
      providesTags: (_result, _error, chatId) => [{ type: "Reports", id: chatId }]
    }),

    configureReports: builder.mutation<UpsertReportsResponse, { chatId: number; request: UpsertReportsRequest }>({
      query: ({ chatId, request }) => ({
        url: `/api/v1/moderation/reports/${chatId}`,
        method: "PUT",
        body: request
      }),
      invalidatesTags: (_, error, { chatId }) => (error ? [] : [{ type: "Reports", id: chatId }])
    }),

    enableReports: builder.mutation<undefined, number>({
      query: chatId => ({
        url: `/api/v1/moderation/reports/${chatId}/enable`,
        method: "PATCH"
      }),
      invalidatesTags: (_, error, chatId) =>
        error
          ? []
          : [
              { type: "Reports", id: chatId },
              { type: "FeaturesStatus", id: chatId }
            ]
    }),

    disableReports: builder.mutation<undefined, number>({
      query: chatId => ({
        url: `/api/v1/moderation/reports/${chatId}/disable`,
        method: "PATCH"
      }),
      invalidatesTags: (_, error, chatId) =>
        error
          ? []
          : [
              { type: "Reports", id: chatId },
              { type: "FeaturesStatus", id: chatId }
            ]
    }),

    // Returns { ok: true } on success or { ok: false, error: [...] } on validation failure (both HTTP 200)
    validateMarkup: builder.mutation<ValidateMarkupResponse, ValidateMarkupRequest>({
      query: request => ({
        url: `/api/v1/moderation/validate-markup`,
        method: "POST",
        body: request
      })
    })
  })
})

export const {
  useCreateButtonEmojiCaptureIntentMutation,
  useCancelButtonEmojiCaptureIntentMutation,
  useGetButtonEmojiCaptureQuery,
  useGetGreetingQuery,
  useGetQuietModeQuery,
  useUpdateQuietModeMutation,
  useUpdateQuietModeMessageMutation,
  useActivateMessageCaptureIntentMutation,
  useCreateQuietModeCaptureIntentMutation,
  useCreateGreetingCaptureIntentMutation,
  useConfigureGreetingMutation,
  useConfigureGreetingWithStatusMutation,
  useUploadGreetingMediaMutation,
  useUpdateGreetingSettingsMutation,
  useDeleteGreetingMutation,
  useEnableGreetingMutation,
  useDisableGreetingMutation,
  useGetFarewellQuery,
  useCreateFarewellCaptureIntentMutation,
  useConfigureFarewellMutation,
  useConfigureFarewellWithStatusMutation,
  useUploadFarewellMediaMutation,
  useUpdateFarewellSettingsMutation,
  useDeleteFarewellMutation,
  useEnableFarewellMutation,
  useDisableFarewellMutation,
  useGetLeavePunishmentQuery,
  useUpdateLeavePunishmentMutation,
  useGetRulesQuery,
  useCreateRulesCaptureIntentMutation,
  useConfigureRulesMutation,
  useConfigureRulesWithStatusMutation,
  useUploadRulesMediaMutation,
  useUpdateRulesSettingsMutation,
  useDeleteRulesMutation,
  useEnableRulesMutation,
  useDisableRulesMutation,
  useGetAutoMessageQuery,
  useGetChatAutoMessagesQuery,
  useCreateAutoMessageCaptureIntentMutation,
  useConfigureAutoMessageMutation,
  useUploadAutoMessageMediaMutation,
  useUpdateAutoMessageSettingsMutation,
  usePreviewAutoMessageScheduleQuery,
  useDeleteAutoMessageMutation,
  usePauseAutoMessageMutation,
  useContinueAutoMessageMutation,
  useGetReportsQuery,
  useConfigureReportsMutation,
  useEnableReportsMutation,
  useDisableReportsMutation,
  useValidateMarkupMutation
} = moderationApi
